import { useEffect, useMemo, useState } from 'react';
import { Spotlight, SpotlightActionGroupData } from '@mantine/spotlight';
import { useDebouncedValue } from '@mantine/hooks';
import { IconSearch } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';

import { useViews } from '../../hooks/QueryHooks';
import { getGlobalSearch } from '../../services/globalSearchEndpoint';
import useWorkspaceStore from '../../store/workspace/workspaceStore';
import { Avatar, Loader } from '@mantine/core';

/**
 * GlobalSpotlight renders Mantine <Spotlight> and provides dynamic actions
 * fetched from the existing global search endpoint. It preserves the exact
 * behaviour previously implemented in `TopSearchBar.tsx`.
 */
export default function GlobalSpotlight() {
  const params = useParams();
  const navigate = useNavigate();
  const workspaceId = useWorkspaceStore((s) => s.workspace?.id);

  const { data: views }: any = useViews({ workspaceId, enabled: !!workspaceId });

  // Keep default views for accounts & users (identical logic to legacy code)
  const defaultView = useMemo(() => {
    if (!Array.isArray(views)) return { accounts: null, users: null } as any;
    return views
      .filter((v: any) => v?.is_precreated)
      .reduce((acc: any, v: any) => {
        if (v?.entity_relation) acc[v.entity_relation] = v;
        return acc;
      }, {} as any);
  }, [views]);

  const [query, setQuery] = useState('');
  const [debounced] = useDebouncedValue(query, 600);
  const [actions, setActions] = useState<SpotlightActionGroupData[]>([]);
  const [loading, setLoading] = useState(false);

  // Loader shown while fetching data
  const loaderActions = useMemo<SpotlightActionGroupData[]>(
    () => [
      {
        group: '',
        actions: [
          {
            id: 'loading',
            label: 'Loading...',
            description: '',
            leftSection: <Loader size="xs" />,
            onTrigger: () => {},
            closeSpotlightOnTrigger: false,
          },
        ],
      },
    ],
    [],
  );

    // Convert API payload to SpotlightActionGroupData[] grouped by category
    const transformResults = (results: Record<string, any[]>): SpotlightActionGroupData[] =>
      Object.entries(results).map(([category, items]) => ({
        group: `${category.charAt(0).toUpperCase()}${category.slice(1)}`,
        actions: items.map((item) => ({
          id: `${category}-${item.external_id ?? item.id}`,
          label: item.identifier,
          description: category,
          leftSection: (
              <Avatar
                size="sm"
                variant="filled"
                color="initials"
                name={item?.identifier || item?.name || item?.view_name}
                src={item?.avatar_url}
              />
            ),
          onClick: () => {
            if (!params.workspaceId) return;
            if (category === 'accounts') {
              const viewId = defaultView?.accounts?.id;
              if (viewId) {
                navigate(`/home/${params.workspaceId}/view/${viewId}/account/${item.external_id}/overview`);
              }
            } else if (category === 'contacts') {
              const viewId = defaultView?.users?.id;
              if (viewId) {
                navigate(`/home/${params.workspaceId}/view/${viewId}/user/${item.external_id}`);
              }
            } else if (category === 'views') {
              navigate(`/home/${params.workspaceId}/view/${item.external_id}`);
            }
            setQuery('');
          },
        })),
      }));

  // Fetch results whenever debounced query changes
  useEffect(() => {
    const fetchData = async () => {
      if (!workspaceId || debounced.length < 2) {
        setActions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const response = await getGlobalSearch({
          search_query: debounced,
          workspace_identifier: workspaceId,
          limit: 20,
        });
        const results = response?.data?.results || {};
        setActions(transformResults(results));
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Global search error', err);
        setActions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [debounced, workspaceId]);

  return (
    <Spotlight
      actions={loading ? loaderActions : actions}
      nothingFound="No results found"
      highlightQuery
      limit={10}
      searchProps={{
        leftSection: <IconSearch size={18} stroke={1.5} />,
        placeholder: 'Search…',
        value: query,
        onChange: (event) => setQuery(event.currentTarget.value),
      }}
      shortcut={null} // disable internal shortcut, handled globally elsewhere
    />
  );
}
